import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import { getRefid } from 'customer-flows/services/quote-builder'
import { DateTime } from 'luxon'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'


export default class BookingQuoteStoreClass extends BaseStore {
  @service intl: IntlService

  @context('quote') quote: QuoteStore['quote']
  @context('program/booking-flow') overrideFreesaleText: BookingFlowStore['overrideFreesaleText']

  get bookingQuotes(): BookingQuote[] {
    return this.quote.value?.bookingQuotes ?? []
  }

  get tippingConfig(): TippingConfig {
    return this.quote.value?.tippingConfig ?? {}
  }

  get tipOptions() {
    return this.tippingConfig.tipOptions ?? []
  }

  get tipMessage(): string | undefined {
    return this.tippingConfig.message
  }

  get tipCurrency(): string {
    return this.tipOptions[0]?.value?.currency ?? ''
  }

  get selectedDateTimeDuration(): string {
    const durationSnapshot = this.getDurationSnapshot()
    const timeSnapshot = this.getTimeSnapshot()

    if (durationSnapshot?.length.unit === 'FLEXIBLE') {
      const anytimeText = this.overrideFreesaleText ?? this.intl.t('common.anytime')
      const date = DateTime.fromISO(timeSnapshot?.from ?? '')

      if (date.isValid) {
        const dateStr = date.toLocaleString({ year: 'numeric', weekday: 'short', month: 'short', day: '2-digit' })
        return `${dateStr}, ${anytimeText}`
      }

      return anytimeText
    }

    // this covers loading states; when there is no timeSnapshot, we just don't display anything
    if (!timeSnapshot) {
      return ''
    }

    // loading is done, we can know if the timeSnapshot is valid or not
    const from = DateTime.fromISO(timeSnapshot.from ?? '')
    const until = DateTime.fromISO(timeSnapshot.until ?? '')

    if (!from.isValid || !until.isValid) {
      return this.intl.t('common.invalid-date-time')
    }

    const span = from.until(until)

    return span.toLocaleString({
      year: 'numeric',
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Returns the bookingQuote for a given refid.
   * @param ref (optional) The refid or ref name of the booking quote (e.g., 'bookingQuotes:addons', 'fdff5f2c-08e8-47d8-e8fd-609a6a55a786')
   *
   * If no ref is provided, it will return the default booking quote.
   */
  getBookingQuoteForRef(ref?: string): BookingQuote | undefined {
    const bookingQuotes = this.bookingQuotes
    const refName = 'bookingQuotes' + (ref ? `:${ref}` : '')
    const refid = getRefid(refName) ?? ref

    return bookingQuotes.find((quote) => {
      return quote.refid === refid
    })
  }

  getActivitySnapshot(ref?: string) {
    return this.getBookingQuoteForRef(ref)?.activitySnapshot
  }

  getLines(ref?: string) {
    return this.getBookingQuoteForRef(ref)?.lines ?? []
  }

  getTimeSnapshot(ref?: string) {
    return this.getBookingQuoteForRef(ref)?.timeSnapshot
  }

  getDurationSnapshot(ref?: string) {
    return this.getBookingQuoteForRef(ref)?.durationSnapshot
  }
}

declare global {
  export type BookingQuoteStore = BookingQuoteStoreClass
  export interface StoreRegistry {
    'quote/booking-quote': BookingQuoteStore
  }
}
